/**
 * El menu lateral: la lista de lo que esta aplicacion sabe hacer.
 *
 * ===========================================================================
 * ENLACES DE VERDAD, NO BOTONES
 *
 * Cada zona es un `<a href="#/...">`. Podrian ser botones --el estado lo lleva
 * la aplicacion igual-- y serian peores por cuatro cosas concretas: no se
 * pueden copiar con el boton derecho, no se abren en otra pestana, el navegador
 * no los marca como visitados y un lector de pantalla los anuncia como
 * controles en vez de como navegacion. Que la URL cambie ya estaba decidido
 * [zonas.ts]; usar el elemento que existe para eso es la consecuencia.
 *
 * `aria-current="page"` marca la zona activa. La clase `.zona-activa` la pinta;
 * el atributo es lo que la dice en voz alta.
 *
 * ===========================================================================
 * EL CONTADOR NO ES DECORACION
 *
 * Las colas --altas de proveedor, rate sheets sin firmar-- no avisan a nadie:
 * no hay envio de correo y nadie mira una pantalla que no sabe que tiene cosas
 * dentro. El numero al lado del nombre es lo unico que convierte "hay una zona
 * de Tarifas" en "hay once documentos esperando tu firma".
 *
 * Se pinta solo cuando es mayor que cero. Un "(0)" permanente es ruido que se
 * deja de leer, y entonces el "(11)" tampoco se lee.
 *
 * ===========================================================================
 * EN MOVIL SE PLIEGA, Y EL BOTON VIVE EN LA CABECERA
 *
 * El menu no se esconde solo con CSS: `aria-hidden` y el foco tienen que
 * seguirlo, o un lector de pantalla lee siete enlaces invisibles. Lo abre y lo
 * cierra `alternarMenu` desde `app.ts`, que es quien tiene el estado; aqui solo
 * se pinta el resultado.
 */

import { el } from '../dom';
import type { Traductor } from '../i18n';
import type { Zona } from '../zonas';
import { rutaDeZona, type DefinicionZona } from '../zonas';

export interface OpcionesMenu {
  readonly t: Traductor;
  readonly zonas: readonly DefinicionZona[];
  readonly activa: Zona;
  /** Cuantas cosas esperan en cada zona. Solo las que tienen cola. */
  readonly pendientes: Partial<Record<Zona, number>>;
  /** Si esta desplegado. En escritorio siempre lo esta --lo fuerza el CSS-- y
   *  esto solo manda en movil. */
  readonly abierto: boolean;
  readonly onIr: (zona: Zona) => void;
}

export function pintarMenu(o: OpcionesMenu): HTMLElement {
  const { t } = o;

  const enlaces = o.zonas.map((z) => {
    const n = o.pendientes[z.id] ?? 0;
    const activa = z.id === o.activa;

    const enlace = el(
      'a',
      {
        clase: `menu-enlace ${activa ? 'zona-activa' : ''}`,
        attrs: {
          href: rutaDeZona(z.id),
          ...(activa ? { 'aria-current': 'page' } : {}),
        },
      },
      [
        el('span', { clase: 'menu-nombre', texto: t(`zona.${z.id}.nombre`) }),
        // El numero solo si hay algo. Ver la cabecera.
        n === 0
          ? null
          : el('span', {
              clase: 'menu-contador',
              texto: String(n),
              // El numero solo dice "11". El titulo dice de que.
              attrs: { title: t(`zona.${z.id}.pendientes`, { n: String(n) }) },
            }),
      ],
    );

    // Se intercepta el clic para no depender de `hashchange`, que en algunos
    // navegadores llega un tick despues y deja la pantalla vieja un instante.
    // El `href` se queda: es lo que hace que el enlace se pueda copiar, y sigue
    // funcionando si esto no corre --una recarga en esa URL aterriza igual--.
    enlace.addEventListener('click', (ev) => {
      // Con Ctrl, Cmd o el boton central el navegador abre otra pestana. Ahi no
      // se intercepta nada: interceptarlo abriria la zona aqui y no alli.
      const e = ev as MouseEvent;
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      ev.preventDefault();
      o.onIr(z.id);
    });

    return el('li', { clase: 'menu-item' }, [enlace]);
  });

  return el(
    'nav',
    {
      clase: `menu ${o.abierto ? 'menu-abierto' : ''}`,
      attrs: {
        id: 'menu-zonas',
        'aria-label': t('menu.titulo'),
      },
    },
    [el('ul', { clase: 'menu-lista' }, enlaces)],
  );
}

/**
 * El boton que lo pliega. Vive en la cabecera y no dentro del menu: un boton
 * para abrir algo que esta dentro de lo que abre no se alcanza cuando esta
 * cerrado.
 */
export function pintarAlternarMenu(
  t: Traductor,
  abierto: boolean,
  onAlternar: () => void,
): HTMLElement {
  return el('button', {
    clase: 'boton boton-plano menu-alternar',
    texto: '☰',
    attrs: {
      type: 'button',
      'aria-controls': 'menu-zonas',
      'aria-expanded': abierto ? 'true' : 'false',
      // El simbolo no lo lee un lector de pantalla como "menu": lee "trigrama
      // del cielo". La etiqueta accesible es la que dice que es.
      'aria-label': t('menu.alternar'),
      title: t('menu.alternar'),
    },
    onClick: onAlternar,
  });
}
